import apiClient from './apiClient';
import { withActingAs } from './actingAs';
import type {
  AnalyticsFilters,
  AnalyticsSummary,
  BreakdownResponse,
  ByTimeResponse,
  CapitalHistoryResponse,
  DailyPnlPoint,
  DistributionResponse,
  PerBasketResponse,
  PositionsInRangeResponse,
  RollingWinRateResponse,
  StreaksResponse,
  TopTradesResponse,
} from '../types/analytics';

const API_TYPE = 'api' as const;

function buildQuery(filters: AnalyticsFilters, extra: Record<string, string | number> = {}): string {
  const params = new URLSearchParams();
  params.set('start', filters.start);
  params.set('end', filters.end);
  for (const a of filters.accounts ?? []) params.append('accounts', a);
  for (const b of filters.baskets ?? []) params.append('baskets', b);
  for (const s of filters.strategies ?? []) params.append('strategies', s);
  for (const [k, v] of Object.entries(extra)) params.set(k, String(v));
  withActingAs(params);
  return `?${params.toString()}`;
}

class AnalyticsService {
  async getSummary(filters: AnalyticsFilters): Promise<AnalyticsSummary> {
    return apiClient.get<AnalyticsSummary>(
      `/analytics/summary${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getEquityCurve(filters: AnalyticsFilters) {
    return apiClient.get<{ points: Array<{ date: string; equity: number; drawdown: number }> }>(
      `/analytics/equity-curve${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getDailyPnl(filters: AnalyticsFilters) {
    // DailyPnlPoint includes `trades` (emitted by /analytics/daily-pnl) — the
    // Daily P&L table filters on it to drop zero-trade days.
    return apiClient.get<{ points: DailyPnlPoint[] }>(
      `/analytics/daily-pnl${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getHeatmap(filters: AnalyticsFilters) {
    return apiClient.get<{ months: Array<{ year: number; month: number; pnl: number; pctReturn: number | null }> }>(
      `/analytics/heatmap${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getBreakdown(filters: AnalyticsFilters,
                     dimension: 'strategy' | 'basket' | 'underlying' | 'exit_reason'): Promise<BreakdownResponse> {
    return apiClient.get<BreakdownResponse>(
      `/analytics/breakdown${buildQuery(filters, { dimension })}`, undefined, API_TYPE
    );
  }

  async getDistribution(filters: AnalyticsFilters, bins = 20): Promise<DistributionResponse> {
    return apiClient.get<DistributionResponse>(
      `/analytics/distribution${buildQuery(filters, { bins })}`, undefined, API_TYPE
    );
  }

  async getTopTrades(filters: AnalyticsFilters,
                     direction: 'winners' | 'losers', limit = 10): Promise<TopTradesResponse> {
    return apiClient.get<TopTradesResponse>(
      `/analytics/top-trades${buildQuery(filters, { direction, limit })}`, undefined, API_TYPE
    );
  }

  async getStreaks(filters: AnalyticsFilters): Promise<StreaksResponse> {
    return apiClient.get<StreaksResponse>(
      `/analytics/streaks${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getByTime(filters: AnalyticsFilters, dimension: 'dow' | 'hour' | 'dte'): Promise<ByTimeResponse> {
    return apiClient.get<ByTimeResponse>(
      `/analytics/by-time${buildQuery(filters, { dimension })}`, undefined, API_TYPE
    );
  }

  async getCapitalHistory(filters: AnalyticsFilters): Promise<CapitalHistoryResponse> {
    return apiClient.get<CapitalHistoryResponse>(
      `/analytics/capital-history${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getRollingWinRate(filters: AnalyticsFilters, window = 30): Promise<RollingWinRateResponse> {
    return apiClient.get<RollingWinRateResponse>(
      `/analytics/rolling-win-rate${buildQuery(filters, { window })}`, undefined, API_TYPE
    );
  }

  async getPositionsInRange(filters: AnalyticsFilters): Promise<PositionsInRangeResponse> {
    return apiClient.get<PositionsInRangeResponse>(
      `/analytics/positions${buildQuery(filters)}`, undefined, API_TYPE
    );
  }

  async getPerBasket(filters: AnalyticsFilters): Promise<PerBasketResponse> {
    return apiClient.get<PerBasketResponse>(
      `/analytics/per-basket${buildQuery(filters)}`, undefined, API_TYPE
    );
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
